// 증상 예측 카드 컴포넌트
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SymptomPrediction } from '../../util/bcg/types';

interface SymptomPredictionCardProps {
  prediction: SymptomPrediction;
}

export const SymptomPredictionCard: React.FC<SymptomPredictionCardProps> = ({ prediction }) => {
  const { t } = useTranslation();
  
  const getRiskColor = (level: string) => {
    switch (level) {
      case 'low':
        return {
          bg: 'bg-emerald-50',
          border: 'border-emerald-200',
          text: 'text-emerald-800',
          badge: 'bg-emerald-500',
          progress: 'bg-emerald-500'
        };
      case 'moderate':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-800',
          badge: 'bg-blue-500',
          progress: 'bg-blue-500'
        };
      case 'high':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-800',
          badge: 'bg-amber-500',
          progress: 'bg-amber-500'
        };
      case 'very_high':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-800',
          badge: 'bg-red-500',
          progress: 'bg-red-500'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          badge: 'bg-gray-500',
          progress: 'bg-gray-500'
        };
    }
  };

  const getRiskLabel = (level: string) => {
    switch (level) {
      case 'low': return t('mentalHealth.symptom.level.normal');
      case 'moderate': return t('mentalHealth.status.caution');
      case 'high': return t('mentalHealth.status.caution');
      case 'very_high': return t('mentalHealth.status.risk');
      default: return t('mentalHealth.status.noInfo');
    }
  };

  const colors = getRiskColor(prediction.riskLevel);

  return (
    <div className={`${colors.bg} border-2 ${colors.border} rounded-2xl p-6 transition-all hover:shadow-lg`}>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="text-4xl">{prediction.icon}</div>
          <div>
            <h4 className={`text-xl font-bold ${colors.text}`}>
              {t(prediction.symptomName)}
            </h4>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white ${colors.badge}`}>
                {getRiskLabel(prediction.riskLevel)}
              </span>
              {prediction.confidence < 0.8 && (
                <span className="text-xs text-gray-500">
                  ({t('common.confidence')}: {Math.round(prediction.confidence * 100)}%)
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-black ${colors.text}`}>
            {prediction.confidence > 0 ? (
              100 - Math.round(prediction.score)
            ) : (
              <span className="text-lg">{t('common.analysis.unavailable')}</span>
            )}
          </div>
          {prediction.confidence > 0 && (
            <div className="text-xs text-gray-500">{t('mentalHealth.score.points')}</div>
          )}
        </div>
      </div>

      {/* 점수 바 */}
      <div className="mb-4">
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`${prediction.confidence > 0 ? colors.progress : 'bg-gray-300'} h-3 rounded-full transition-all duration-500`}
            style={{ width: `${prediction.confidence > 0 ? 100 - Math.min(100, prediction.score) : 100}%` }}
          ></div>
        </div>
      </div>

      {/* 설명 */}
      <p className={`${colors.text} text-sm leading-relaxed mb-4`}>
        {t(prediction.description)}
      </p>

      {/* 지표들 */}
      {prediction.indicators.length > 0 && prediction.indicators[0] !== 'symptom.indicator.insufficientData' && prediction.indicators[0] !== 'symptom.indicator.normalRange' && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-gray-600 mb-2">{t('common.indicators')}:</div>
          <div className="flex flex-wrap gap-2">
            {prediction.indicators.map((indicator, index) => (
              <span
                key={index}
                className={`inline-flex items-center px-3 py-1 rounded-lg text-xs ${colors.bg} ${colors.text} border ${colors.border}`}
              >
                • {t(indicator)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 주요 지표 값 표시 */}
      {prediction.metrics && (
        <div className="mt-4 pt-4 border-t border-gray-200/60">
          <div className="text-xs font-semibold text-gray-600 mb-2">{t('common.keyMetrics')}:</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {Object.entries(prediction.metrics).map(([key, value]: [string, string | number]) => (
              <div key={key} className="flex justify-between text-xs">
                <span className="text-gray-500">{key}</span>
                <span className={`font-bold ${colors.text}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 데이터 부족 경고 */}
      {prediction.confidence === 0 && (
        <div className="mt-4 bg-gray-100 border border-gray-300 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <span className="text-gray-600 text-sm">⚠️ {t('common.insufficientData')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

